// ROARA product gallery: one large image + a vertical thumbnail rail.
//
// Horizon's native "carousel" presentation with thumbnail/dot controls
// (media_presentation: carousel) was tested directly against this
// theme and found non-functional here — clicking a thumbnail or the
// native next/previous arrows updates the control's own selected state
// but never advances the visible slide, with no console error to
// explain why, reproduced with both "thumbnails" and "dots" control
// styles. templates/product.json therefore uses the proven-reliable
// "grid" presentation (media_columns: "one") instead, which simply
// stacks every real product image in a plain list with no carousel JS
// involved — and this script builds the single-image + thumbnail-rail
// interaction directly on top of that real, Shopify-rendered markup.
//
// Nothing here creates, duplicates in a lossy way, or invents a product
// image: each thumbnail is a small <img> pointed at the exact same
// currentSrc Shopify already rendered for that slide, and switching
// images only ever toggles which of the real, native .product-media-
// container elements is visible — never regenerates or re-fetches
// anything.
//
// Root cause of the "image zooms/jumps when a size is selected" bug:
// Horizon's own <media-gallery> custom element (assets/media-gallery.js,
// #handleProductSelect) completely discards and replaces itself with a
// brand-new element fetched from the server on every variant change —
// confirmed directly by watching the DOM across a real size click: the
// original node is removed and a fresh, un-enhanced one takes its place.
// This script used to run initGallery() exactly once, at the initial
// page load, so that fresh replacement element never got the rail/
// single-image treatment below — it fell back to Shopify's raw native
// "grid" markup (every image stacked at full, unconstrained width), which
// is what actually produced the oversized/cropped image and page jump:
// not a missing CSS rule, a missing re-initialization. The MutationObserver
// at the bottom of this file re-runs initGallery on every later
// <media-gallery> Horizon swaps in, for exactly this reason — the same
// pattern already used in curlycubs-homepage-card-swipe.js for the
// homepage grid's own dynamically-replaced sections.
(() => {
  const initGallery = (gallery) => {
    if (gallery.dataset.ccGalleryReady) return;

    const grid = gallery.querySelector('.media-gallery__grid');
    if (!grid) return;

    const containers = [...grid.children].filter((el) => el.classList.contains('product-media-container'));
    // Nothing to switch between (e.g. a product with only one image) —
    // leave the native single-image rendering exactly as Shopify built it.
    if (containers.length < 2) return;

    const rail = document.createElement('div');
    rail.className = 'cc-product-gallery__rail';

    const buttons = [];
    let activeIndex = 0;

    const setActive = (index) => {
      activeIndex = index;
      containers.forEach((container, i) => {
        // Inline style (not the `hidden` attribute) so this always wins
        // regardless of any class-based `display` rule elsewhere —
        // .product-media-container sets display:flex natively, which
        // would otherwise silently defeat `[hidden]`.
        container.style.display = i === index ? '' : 'none';
      });
      buttons.forEach((button, i) => {
        button.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        button.classList.toggle('cc-product-gallery__thumb--active', i === index);
      });
    };

    containers.forEach((container, index) => {
      const sourceImg = container.querySelector('img');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cc-product-gallery__thumb';
      button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-label', `Show image ${index + 1} of ${containers.length}`);

      if (sourceImg) {
        const thumbImg = document.createElement('img');
        thumbImg.src = sourceImg.currentSrc || sourceImg.src;
        thumbImg.alt = '';
        thumbImg.loading = 'lazy';
        button.appendChild(thumbImg);
      }

      button.addEventListener('click', () => setActive(index));
      buttons.push(button);
      rail.appendChild(button);
    });

    gallery.dataset.ccGalleryReady = 'true';
    gallery.classList.add('cc-product-gallery');
    gallery.insertBefore(rail, grid);
    setActive(0);

    // Touch swipe on the main image area — the native carousel would
    // normally provide this; since it's switched off here, this is a
    // small, direct replacement built on the same setActive the
    // thumbnails already use, not a separate code path to maintain.
    let touchStartX = null;

    grid.addEventListener('touchstart', (event) => {
      touchStartX = event.touches[0].clientX;
    }, { passive: true });

    grid.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const deltaX = event.changedTouches[0].clientX - touchStartX;
      touchStartX = null;

      const SWIPE_THRESHOLD = 40;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

      if (deltaX < 0 && activeIndex < containers.length - 1) {
        setActive(activeIndex + 1);
      } else if (deltaX > 0 && activeIndex > 0) {
        setActive(activeIndex - 1);
      }
    }, { passive: true });
  };

  document.querySelectorAll('media-gallery').forEach(initGallery);

  // Catches every later <media-gallery> Horizon swaps in via
  // replaceWith() (see the root-cause note above) — a plain childList
  // observer, not an event listener on Horizon's own product-select
  // event, because by the time that event's promise resolves the
  // replacement has already happened; watching the DOM directly means
  // this always runs exactly once the new element actually exists,
  // with no race against Horizon's own timing.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('media-gallery')) {
          initGallery(node);
        } else {
          node.querySelectorAll?.('media-gallery').forEach(initGallery);
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
